import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useRef, useEffect } from 'react';
import { KeyboardKey } from '../../../enums/keyboardKey.js';
import { FOCUSABLE_SELECTORS } from '../../../constants/focusableSelectors.js';

const focusableSelectors = FOCUSABLE_SELECTORS.join(', ');
const styles = {
    backdrop: (isOpen) => `z-10 fixed inset-0 bg-neutral-700 transition-opacity duration-300 ${isOpen ? 'opacity-50' : 'opacity-0 pointer-events-none'} `,
    sideDrawContainer: 'z-50 fixed inset-x-0 bottom-0 pointer-events-none tablet:inset-y-0 tablet:inset-x-auto tablet:right-0 tablet:top-0 tablet:w-126.25',
    sideDraw: (isOpen) => `shadow-[-4px_4px_10px_0_rgba(0,0,0,0.25)] ${isOpen ? 'translate-y-0 tablet:translate-x-0 pointer-events-auto' : 'translate-y-full tablet:translate-y-0 tablet:translate-x-full'} flex w-full flex-col gap-2 bg-bg-main-container tablet:gap-space-400 overflow-y-auto transition-transform duration-300 ease-in-out`,
    buttonContainer: 'w-full flex justify-end',
    button: 'cursor-pointer mr-space-700',
};
/**
 * Renders a responsive side panel with backdrop, focus trapping, and keyboard support.
 *
 * Behavior:
 * - Mobile: opens from the bottom.
 * - Tablet and above: opens from the right.
 * - Locks body scroll while open and compensates for scrollbar width to prevent layout shift.
 * - Closes on backdrop click and when the Escape key is pressed.
 * - Keeps keyboard focus cycling within focusable elements inside the panel when open.
 *
 * @component
 * @param {SlidingPanelProps} props - Component props.
 * @param {boolean} props.isOpen Controls whether the panel is visible.
 * @param {() => void} props.onClose Callback invoked when the panel should close.
 * @param {React.ReactNode} props.children Content rendered inside the panel.
 * @param {'fit-content' | 'full'} props.height Panel height mode. full fills viewport height, fit-content wraps to the height of the content.
 *
 * @example
 * <SlidingPanel isOpen={isPanelOpen} onClose={handleClose} height="fit-content">
 *   <p>Panel Content</p>
 * </SlidingPanel>
 *
 * @remarks
 * - Focus trapping ensures that keyboard navigation remains within the panel while it is open.
 * - The `Escape` key closes the panel, and `Tab` key navigation is managed for accessibility.
 * @accessibility
 * - The panel uses `aria-hidden` to indicate its visibility state.
 * - Focusable elements within the panel are managed to ensure proper keyboard navigation.
 *
 **/
const SlidingPanel = ({ isOpen, onClose, children, height = 'full' }) => {
    const dialogRef = useRef(null);
    const displayHeight = height === 'full' ? 'h-full' : 'h-fit max-h-screen';
    useEffect(() => {
        if (!isOpen)
            return;
        //Compensate for scrollbar disappearance  when assigning overflow hidden
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = `${scrollbarWidth}px`;
        return () => {
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        };
    }, [isOpen]);
    useEffect(() => {
        if (!isOpen || !dialogRef.current)
            return;
        const dialog = dialogRef.current;
        const handleKeyDown = (e) => {
            if (e.key === KeyboardKey.Escape) {
                onClose();
                return;
            }
            if (e.key === KeyboardKey.Tab) {
                const focusable = Array.from(dialog.querySelectorAll(focusableSelectors));
                if (focusable.length === 0) {
                    e.preventDefault();
                    return;
                }
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                const active = document.activeElement;
                if (e.shiftKey) {
                    if (active === first || !dialog.contains(active)) {
                        e.preventDefault();
                        last.focus();
                    }
                }
                else {
                    if (active === last || !dialog.contains(active)) {
                        e.preventDefault();
                        first.focus();
                    }
                }
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);
    return (jsxs(Fragment, { children: [jsx("div", { className: styles.backdrop(isOpen), onClick: onClose }), jsx("div", { className: styles.sideDrawContainer + ' ' + `${displayHeight}`, children: jsxs("div", { className: styles.sideDraw(isOpen) + ' ' + `${displayHeight}`, children: [jsx("div", { ref: dialogRef, className: styles.buttonContainer, tabIndex: isOpen ? 0 : -1, "aria-hidden": !isOpen }), children] }) })] }));
};

export { SlidingPanel };
